import { useState, useEffect } from "react";
import type { Customer } from "../../lib/types";
import { adminSearchCustomers, adminAdjustCredit, adminGetCustomerCredit, adminGetRecentCustomers } from "../../lib/hooks";

interface CreditTransaction {
  id: number;
  amount_cents: number;
  type: string;
  description: string | null;
  created_at: string;
}

export function ManageCredits() {
  const [query, setQuery] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [recentCustomers, setRecentCustomers] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Customer | null>(null);

  // Load recent customers on mount
  useEffect(() => {
    adminGetRecentCustomers().then(({ data }) => {
      if (data && Array.isArray(data)) {
        setRecentCustomers(data as Customer[]);
      }
    });
  }, []);
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loadingCredit, setLoadingCredit] = useState(false);

  // Adjust form
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustDesc, setAdjustDesc] = useState("");
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustMessage, setAdjustMessage] = useState("");

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    const { data } = await adminSearchCustomers(query.trim());
    setCustomers((data as Customer[]) ?? []);
    setSearching(false);
  };

  const handleSelect = async (customer: Customer) => {
    setSelected(customer);
    setAdjustMessage("");
    setLoadingCredit(true);
    const { data } = await adminGetCustomerCredit(customer.user_id);
    if (data) {
      setBalance(data.balance_cents ?? 0);
      setTransactions(data.transactions ?? []);
    }
    setLoadingCredit(false);
  };

  const handleAdjust = async (positive: boolean) => {
    const euros = parseFloat(adjustAmount);
    if (isNaN(euros) || euros <= 0) {
      setAdjustMessage("Zadajte platnú sumu v €");
      return;
    }
    const cents = Math.round(euros * 100) * (positive ? 1 : -1);
    const desc = adjustDesc.trim() || (positive ? "Manuálne pridanie kreditu" : "Manuálne odobranie kreditu");

    setAdjustLoading(true);
    setAdjustMessage("");
    const { data, error } = await adminAdjustCredit(selected!.user_id, cents, desc);
    if (error) {
      setAdjustMessage(`Chyba: ${error.message}`);
    } else {
      setBalance(data?.balance_cents ?? 0);
      setAdjustMessage(`Kredit upravený. Nový zostatok: ${((data?.balance_cents ?? 0) / 100).toFixed(2).replace(".", ",")} €`);
      setAdjustAmount("");
      setAdjustDesc("");
      // Refresh transactions
      const { data: refreshed } = await adminGetCustomerCredit(selected!.user_id);
      if (refreshed) setTransactions(refreshed.transactions ?? []);
    }
    setAdjustLoading(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("sk-SK", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const typeLabel = (type: string) => {
    switch (type) {
      case "cancellation_refund": return "Storno";
      case "booking_deduction": return "Rezervácia";
      case "admin_adjustment": return "Admin";
      case "payment_refund": return "Vrátená platba";
      default: return type;
    }
  };

  return (
    <div className="admin-credits">
      <h2>Správa kreditov</h2>

      {/* Search */}
      <div className="admin-credit-search">
        <div className="form-field">
          <label>Hľadať zákazníka</label>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Meno, email alebo členské číslo (napr. 4821)"
            />
            <button className="btn btn-primary btn-small" onClick={handleSearch} disabled={searching}>
              {searching ? "..." : "Hľadať"}
            </button>
          </div>
        </div>

        {(() => {
          const displayList = query.trim() ? customers : recentCustomers;
          if (displayList.length === 0) return null;
          return (
            <div className="admin-customer-list">
              {!query.trim() && <div className="admin-customer-list-label">Posledná aktivita</div>}
              {displayList.map((c) => (
                <button
                  key={c.id}
                  className={`admin-customer-item ${selected?.id === c.id ? "selected" : ""}`}
                  onClick={() => handleSelect(c)}
                >
                  <span className="customer-name">{c.name}</span>
                  <span className="customer-email">{c.email}</span>
                  <span className="customer-code-tag">{c.customer_code}</span>
                </button>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Selected customer */}
      {selected && (
        <div className="admin-credit-detail">
          <h3>
            {selected.name}
            <span className="customer-code-tag">{selected.customer_code}</span>
          </h3>
          <p className="customer-email-display">{selected.email}</p>

          {loadingCredit ? (
            <div className="loading-small">Načítavam...</div>
          ) : (
            <>
              {/* Balance */}
              <div className="admin-credit-balance">
                Kredit: <strong>{(balance / 100).toFixed(2).replace(".", ",")} €</strong>
              </div>

              {/* Adjust form */}
              <div className="admin-adjust-form">
                <div className="form-field">
                  <label>Suma (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)}
                    placeholder="napr. 15.00"
                  />
                </div>
                <div className="form-field">
                  <label>Popis (voliteľný)</label>
                  <input
                    type="text"
                    value={adjustDesc}
                    onChange={(e) => setAdjustDesc(e.target.value)}
                    placeholder="Dôvod úpravy"
                  />
                </div>
                <div className="admin-adjust-actions">
                  <button
                    className="btn btn-primary btn-small"
                    onClick={() => handleAdjust(true)}
                    disabled={adjustLoading}
                  >
                    + Pridať kredit
                  </button>
                  <button
                    className="btn btn-danger btn-small"
                    onClick={() => handleAdjust(false)}
                    disabled={adjustLoading}
                  >
                    − Odobrať kredit
                  </button>
                </div>
                {adjustMessage && (
                  <div className={adjustMessage.startsWith("Chyba") ? "form-error" : "form-success"}>
                    {adjustMessage}
                  </div>
                )}
              </div>

              {/* Transaction history */}
              <div className="admin-credit-history">
                <h4>Posledné transakcie</h4>
                {transactions.length === 0 ? (
                  <p className="empty">Žiadne transakcie.</p>
                ) : (
                  <div className="admin-tx-list">
                    {transactions.map((tx) => (
                      <div key={tx.id} className="admin-tx-item">
                        <span className="admin-tx-date">{formatDate(tx.created_at)}</span>
                        <span className="admin-tx-type">{typeLabel(tx.type)}</span>
                        <span className={`admin-tx-amount ${tx.amount_cents >= 0 ? "positive" : "negative"}`}>
                          {tx.amount_cents >= 0 ? "+" : ""}{(tx.amount_cents / 100).toFixed(2).replace(".", ",")} €
                        </span>
                        {tx.description && <span className="admin-tx-desc">{tx.description}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
